// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import moment from 'moment';
import PropTypes from 'Utils/prop-type-utils';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {Box, DateRangeChip, MultiselectChip, SegmentedControl, Stack, GridTreeDataGroupingCell, EllipsisOverflow, SearchTextProvider, Highlight, GRID_TREE_DATA_GROUPING_FIELD} from '@deere/isg.component-library';
import DataGridWrapper from 'Ui/components/common/data-table/data-grid-wrapper';
import {fetchEffectData, useDeepMemo} from 'Utils/react-utils';
import {getTileData} from 'Services/panel-service';
import {WORKBOARDS} from 'Ui/components/graph/constants/graph-filters';
import {getJobTemplates} from 'Services/job-service';
import {ACTUAL_COST, ACTUAL_DURATION} from 'OnLabor/report/constants/labor-report';
import {DATE_FORMATS} from 'Src/ui-core/utils/time-utils';
import {getDateRangePresets, getMonthKeys, CUSTOM_ROW_STYLE, getDataGridColumns, getFilterItems, getTreeRows, getTotalsRow, mapAreaData, getJobTemplateAreaMap} from '../utils/monthly-labor-report-utils';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {VIEW_FINANCIAL_DATA} from 'Common/constants/business-activities';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import Search from 'Ui/components/search/search';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';

const TAB_TITLE_KEYS = {
    areas: 'AREA',
    jobCategories: 'ONLINK_JOB_CATEGORY'
};

const AREAS_TAB = 'areas';

function filterTreeRows(treeRows, search) {
    const query = search.trim().toLowerCase();

    if (!query) {
        return treeRows;
    }

    const matchingParentIds = new Set(
        treeRows
            .filter(({name}) => name?.toLowerCase().includes(query))
            .map(({path}) => path[0])
    );

    return treeRows.filter(({path}) => matchingParentIds.has(path[0]));
}

function getFilterComponent({
    dateRangePresets,
    dateRangeSelection,
    filterItems,
    isLoading,
    metric,
    setMetric,
    search,
    setSearch,
    selectedIds,
    setSelectedIds,
    tab,
    translations,
    updateDateRange,
    myJdPermissions
}) {
    return (
        <div
            className='extra-table-options'
            style={{
                width: '100%'
            }}
        >
            <Stack
                gap='1rem'
                justifyContent='space-between'
                sx={{
                    alignItems: {
                        xs: 'stretch',
                        sm: 'center'
                    },
                    flexDirection: {
                        xs: 'column',
                        sm: 'row'
                    }
                }}
                width='100%'
            >
                <Box
                    sx={{
                        display: 'flex',
                        minWidth: 0,
                        gap: '1rem',
                        flexDirection: {
                            xs: 'column',
                            sm: 'row'
                        },
                        alignItems: {
                            xs: 'stretch',
                            sm: 'center'
                        }
                    }}
                >
                    <Box
                        sx={{
                            width: {
                                xs: '100%',
                                sm: 'auto'
                            },
                            '& .search-bar-div, & .MuiInputBase-root': {
                                width: {
                                    xs: '100%',
                                    sm: 'auto'
                                }
                            }
                        }}
                    >
                        <Search
                            debounce={500}
                            handler={(event) => setSearch(event.target.value)}
                            search={search}
                            translations={translations}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '1rem'
                        }}
                    >
                        <DateRangeChip
                            dateRangePresets={dateRangePresets}
                            isPersistent={true}
                            onChange={updateDateRange}
                            value={dateRangeSelection}
                        />
                        <MultiselectChip
                            disableCloseOnClick
                            items={filterItems}
                            loading={isLoading}
                            onChange={setSelectedIds}
                            pluralTitle={tab === AREAS_TAB ?
                                translations.AREAS :
                                translations.ONLINK_JOB_CATEGORIES}
                            selectedIds={selectedIds}
                            title={translations[TAB_TITLE_KEYS[tab]]}
                        />
                    </Box>
                </Box>
                {isAuthorized({
                    myJdPermissions: VIEW_FINANCIAL_DATA
                }, {
                    isMigrated: true,
                    myJdPermissions
                }) && <SegmentedControl
                    buttons={[
                        {
                            name: translations.ONLINK_ACT_HRS,
                            onClickHandler: () => setMetric(ACTUAL_DURATION)
                        },
                        {
                            name: translations.COST,
                            onClickHandler: () => setMetric(ACTUAL_COST)
                        }
                    ]}
                    selectedIndex={metric === ACTUAL_COST ? 1 : 0}
                />}
            </Stack>
        </div>
    );
}

function initializeState(selectedDates) {
    const [rollupLoading, setRollupLoading] = React.useState(true);
    const [metric, setMetric] = React.useState(ACTUAL_DURATION);
    const [laborReportData, setLaborReportData] = React.useState({});
    const [jobTemplates, setJobTemplates] = React.useState([]);
    const [templatesLoading, setTemplatesLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [dateRangeSelection, setDateRangeSelection] = React.useState(
        () => selectedDates);
    const {addToast} = useToast();

    return {
        addToast,
        rollupLoading,
        setRollupLoading,
        metric,
        setMetric,
        laborReportData,
        setLaborReportData,
        jobTemplates,
        setJobTemplates,
        templatesLoading,
        setTemplatesLoading,
        selectedIds,
        setSelectedIds,
        dateRangeSelection,
        setDateRangeSelection,
        search,
        setSearch
    };
}

function useMemoizedData({
    startDate,
    endDate,
    jobTemplates,
    laborReportData,
    tab,
    translations,
    metric,
    selectedIds,
    search
}) {
    const monthKeys = React.useMemo(() => getMonthKeys(startDate, endDate), [startDate, endDate]);

    const templateAreaMap = React.useMemo(() => getJobTemplateAreaMap(jobTemplates), [jobTemplates]);

    const reportData = React.useMemo(
        () => tab === AREAS_TAB ? mapAreaData(laborReportData, templateAreaMap, translations) : laborReportData,
        [laborReportData, tab, templateAreaMap, translations]
    );

    const filterItems = React.useMemo(() => getFilterItems(reportData, tab), [reportData, tab]);

    const treeRows = React.useMemo(() => getTreeRows({
        laborReportData: reportData,
        metric,
        monthKeys,
        selectedIds,
        tab
    }), [reportData, metric, monthKeys, selectedIds, tab]);

    const filteredTreeRows = React.useMemo(() => filterTreeRows(treeRows, search), [treeRows, search]);

    const totalsRow = React.useMemo(() => getTotalsRow(filteredTreeRows, monthKeys, translations), [filteredTreeRows, monthKeys, translations]);

    return {
        monthKeys,
        filterItems,
        filteredTreeRows,
        totalsRow
    };
}

function MonthlyLaborReport(props) {
    const {
        isMobile,
        match: {params: {tab}},
        translations,
        membership,
        myJdPermissions
    } = props;

    const dateRangePresets = useDeepMemo(() => getDateRangePresets(translations), [translations]);
    const {
        addToast,
        rollupLoading,
        setRollupLoading,
        metric,
        setMetric,
        laborReportData,
        setLaborReportData,
        jobTemplates,
        setJobTemplates,
        templatesLoading,
        setTemplatesLoading,
        selectedIds,
        setSelectedIds,
        dateRangeSelection,
        setDateRangeSelection,
        search,
        setSearch
    } = initializeState(dateRangePresets[0].getSelectedDates());

    const isLoading = rollupLoading || tab === AREAS_TAB && templatesLoading;

    const [startDate, endDate] = dateRangeSelection.map((date) => moment(date).format(DATE_FORMATS.day));

    const updateDateRange = React.useCallback((newDates) => {
        const [startValue, endValue] = newDates;
        const hasRange = Boolean(startValue) && Boolean(endValue);
        const resolvedDates = hasRange ?
            newDates :
            [moment().startOf('month').toDate(), moment().endOf('month').toDate()];

        setDateRangeSelection(resolvedDates);
    }, [setDateRangeSelection]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        if (tab !== AREAS_TAB) {
            setTemplatesLoading(false);
            return;
        }

        setTemplatesLoading(true);

        try {
            const jobTemplates = (await getJobTemplates()).templates;

            if (isMounted()) {
                setJobTemplates(jobTemplates);
            }
        } catch {
            addToast({
                message: translations.ERROR_OCCURRED_TITLE,
                type: TOAST_TYPE.ERROR
            });
        } finally {
            if (isMounted()) {
                setTemplatesLoading(false);
            }
        }
    }), [membership.membershipId, tab]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setRollupLoading(true);

        try {
            const data = await getTileData(WORKBOARDS, {
                end: endDate,
                start: startDate
            });

            if (isMounted()) {
                setLaborReportData(data ?? {});
            }
        } catch {
            addToast({
                message: translations.ERROR_OCCURRED_TITLE,
                type: TOAST_TYPE.ERROR
            });
        } finally {
            if (isMounted()) {
                setRollupLoading(false);
            }
        }
    }), [membership.membershipId, startDate, endDate, tab]);

    React.useEffect(() => {
        setSelectedIds([]);
    }, [tab]);

    const {
        monthKeys,
        filterItems,
        filteredTreeRows,
        totalsRow
    } = useMemoizedData({
        startDate,
        endDate,
        jobTemplates,
        laborReportData,
        tab,
        translations,
        metric,
        selectedIds,
        search
    });

    return (
        <Box
            padding='1rem'
        >
            <SearchTextProvider value={search}>
                <DataGridWrapper
                    columns={getDataGridColumns({
                        monthKeys,
                        metric,
                        membership,
                        translations
                    })}
                    defaultGroupingExpansionDepth={0}
                    filterComponent={getFilterComponent({
                        dateRangePresets,
                        dateRangeSelection,
                        filterItems,
                        isLoading,
                        metric,
                        setMetric,
                        search,
                        setSearch,
                        selectedIds,
                        setSelectedIds,
                        tab,
                        translations,
                        updateDateRange,
                        myJdPermissions
                    })}
                    getTreeDataPath={(row) => row.path}
                    groupingColDef={{
                        headerName: translations[TAB_TITLE_KEYS[tab]],
                        minWidth: 220,
                        flex: 1.5,
                        valueGetter: (_, row) => row.name,
                        sortComparator: (a, b) => (a ?? '').localeCompare(b ?? '', undefined, {
                            numeric: true,
                            sensitivity: 'base'
                        }),
                        renderCell: (params) => (
                            <GridTreeDataGroupingCell
                                {...params}
                                renderer={(cellParams) => (
                                    <EllipsisOverflow
                                        slotProps={{
                                            typography: {
                                                variant: 'bodyMediumBold'
                                            }
                                        }}
                                        style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            height: '100%',
                                            minWidth: 0
                                        }}
                                        tooltipTitle={cellParams.row.name}
                                    >
                                        <Highlight value={cellParams.row.name}/>
                                    </EllipsisOverflow>
                                )}
                            />
                        )
                    }}
                    loading={isLoading}
                    pinnedColumns={{
                        left: isMobile ? [] : [GRID_TREE_DATA_GROUPING_FIELD],
                        right: []
                    }}
                    rows={filteredTreeRows}
                    searchable={false}
                    slotProps={{
                        root: {
                            disableColumnMenu: true,
                            pinnedRows: {
                                bottom: [totalsRow]
                            }
                        }
                    }}
                    sx={CUSTOM_ROW_STYLE}
                    translations={translations}
                    treeData={true}
                />
            </SearchTextProvider>
        </Box>
    );
}

MonthlyLaborReport.propTypes = {
    isMobile: PropTypes.bool,
    match: PropTypes.match,
    membership: PropTypes.object,
    myJdPermissions: PropTypes.myJdPermissions,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        myJdPermissions: state.account.myJdPermissions
    };
}

export default MediaQuery(MOBILE_MEDIA_QUERY)(withRouter(connect(mapStateToProps)(MonthlyLaborReport)));
